
// @ts-ignore
    import __i18nConfig from '@next-translate-root/i18n'
// @ts-ignore
    import __loadNamespaces from 'next-translate/loadNamespaces'
// @ts-ignore
    
import BasePageLayout from '#layouts/BasePageLayout/BasePageLayout';
import React, { Fragment, useEffect, useState } from 'react';
import SocialMeta from '#components/common/meta/SocialMeta';
import VisibilityVerificationWrapper from '#components/layout/VisibilityVerificationWrapper';
import HeroUnit from '#components/sections/hero-unit/HeroUnit';
import { ContentType, LivestreamState } from '#types/generated/graphql-types';
import HeroButtonParade from '#components/hero-button-parade/HeroButtonParade';
import HeroTabs from '#components/common/heroTabs/HeroTabs';
import Recommendations from '#components/common/recommendations/Recommendations';
import { getRoute, logger } from '#utils';
import IllustratedLayout from '#components/layout/IllustratedLayout';
import useTranslation from 'next-translate/useTranslation';
import useSocket, { Notification } from '#hooks/useSocket';
import {
  GetAnnouncementDocument,
  GetMediaPageDocument,
  GetNavigationDocument,
  useGetMediaPageQuery,
} from '#types/generated/apollo-client';
import dynamic from 'next/dynamic';
import CountdownEndLayout from '#components/layout/livestream/CountdownEndLayout';
import { useRouter } from 'next/router';
import initializeClient from '#providers/apollo/initializeClient';
import addApolloState from '#providers/apollo/addApolloState';

const Countdown = dynamic(() => import('#components/countdown/Countdown'), {
  ssr: false,
});
const InlinePlayer = dynamic(() => import('#modules/player/InlinePlayer'), {
  ssr: false,
});

const playerOptions = {
  controlBar: {
    children: [
      'progressControl',
      {
        name: 'controlBarControls',
        children: [
          'customControlSpacer',
          { name: 'volumePanel', inline: false },
          'fullscreenToggle',
        ],
      },
    ],
  },
};

const LivestreamPage = ({ slug }) => {
  const router = useRouter();
  const { socket } = useSocket();
  const { t } = useTranslation();
  const { data, refetch } = useGetMediaPageQuery({
    variables: { slug, contentType: ContentType.LiveStream },
    fetchPolicy: 'cache-only',
  });

  const handleQueryParamChange = (param: string, value: string) => {
    const { pathname, query } = router; // Get current path and query params
    query[param] = value;
    return router.push({ pathname, query }, undefined, { shallow: true });
  };

  const streamShouldStart =
    data?.mediaBySlug &&
    Date.now() >= new Date(data.mediaBySlug.details?.startDate).getTime();
  const [countDownEnded, setCountDownEnded] = useState(streamShouldStart);

  useEffect(() => {
    if (!socket) return;

    const listener = (msg: Notification) => {
      if (media.iri === msg.iri) {
        logger.debug(`WSS Message received`, { msg });

        handleQueryParamChange('e', msg.id).then(() => {
          //nothing
        });

        refetch({
          eventId: msg.id,
        })
          .then(() => {
            logger.debug(`WSS - Data re-fetched`, { media: msg.iri });
          })
          .catch((e) => {
            logger.debug(`WSS - Data re-fetch error`, { media: msg.iri, e });
          });
      }
    };

    socket.on('livestream_status_update', listener);
    return () => {
      socket.off('livestream_status_update', listener);
    };
  }, [socket]);

  const { mediaBySlug: media } = data ?? {};

  if (!media) {
    return null;
  }

  const showLiveMayHaveStarted =
    [LivestreamState.PreLive, LivestreamState.Upcoming].includes(
      media?.details?.streamState
    ) && countDownEnded;

  const studioUrl = getRoute(media?.studio, t);
  const showBackground =
    !showLiveMayHaveStarted &&
    (media?.details?.streamState === LivestreamState.Upcoming ||
      media?.details?.streamState === LivestreamState.PreLive ||
      media?.details?.streamState === LivestreamState.Archived);

  return (
    <BasePageLayout>
      <Fragment>
        <SocialMeta
          about={{
            title: media.title,
            description: media.teaserDescription,
            image: media?.teaserImageFormats?.sixteenByNine?.medium,
            type: 'video.movie',
          }}
        />

        <VisibilityVerificationWrapper reason={media?.products?.access?.reason} mediaIri={media?.iri}>
          <main className="page-main-container">
            <HeroUnit
              backgroundImageFormats={media?.backgroundImageFormats}
              categories={media?.categories}
              description={media?.shortDescription}
              fskRating={media?.fskRating}
              mediaQuality={media?.mediaQuality}
              mediaDuration={media?.mediaDuration}
              studioLogoImage={media?.studio?.logoImageFormats?.oneByOne?.small}
              studioName={media?.studio?.title}
              studioUrl={studioUrl}
              subtitle={media?.leadin}
              title={media?.title}
              titleImage={media?.logoImageFormats?.sixteenBySeven?.medium}
              year={media?.year}
              showBackground={showBackground}
              streamDetails={media?.details}
              brandLogoImage={media?.logoImageFormats?.oneByOne?.medium}
              buttonComponent={
                <HeroButtonParade
                  mediaIri={media.iri}
                  media={media}
                  showPurchaseButton={
                    media.details.streamState === LivestreamState.Archived
                  }
                  showTrailers={
                    media.details.streamState !== LivestreamState.Live
                  }
                />
              }
            >
              {!countDownEnded &&
                (media.details.streamState === LivestreamState.Upcoming ||
                  media.details.streamState === LivestreamState.PreLive) && (
                  <div className="tw-max-w-page tw-w-full tw-mx-auto tw-mb-7">
                    <Countdown
                      date={media.details.startDate}
                      onCountdownEnd={() => setCountDownEnded(true)}
                    />
                  </div>
                )}

              {media.details.streamState === LivestreamState.OnHold && (
                <div className="tw-max-w-page tw-w-full tw-mx-auto tw-mb-8 medium-up:tw-mb-28">
                  <IllustratedLayout
                    verticalAlignContent="center"
                    image="/static/images/img/Dragon_02_s.png"
                    title={t('livestreamErrorTitleOnHold')}
                    content={t('livestreamErrorDescriptionOnHold')}
                  />
                </div>
              )}

              {countDownEnded && (
                <CountdownEndLayout
                  currentLivestreamState={media?.details?.streamState}
                />
              )}

              {media.details.streamState === LivestreamState.Ended && (
                <div className="tw-max-w-page tw-w-full tw-mx-auto tw-mb-8 medium-up:tw-mb-28">
                  <IllustratedLayout
                    verticalAlignContent="center"
                    image="/static/images/img/euro-maus-ls-ended.png"
                    title={t('livestreamErrorTitleEnded')}
                    content={t('livestreamErrorDescriptionEnded')}
                    pageSectionProps={{ bottomGutter: false }}
                  />
                </div>
              )}

              {media.details.streamState === LivestreamState.Live && (
                <div className="tw-max-w-page tw-w-full tw-mx-auto tw-mb-8 medium-up:tw-mb-28 xlarge-down:tw-px-1">
                  <InlinePlayer mediaIri={media.iri} options={playerOptions} />
                </div>
              )}
            </HeroUnit>
            <HeroTabs
              extras={media?.extras}
              mediaDescription={media.description}
              media={media}
            />
            <Recommendations items={media.recommendations} />
          </main>
        </VisibilityVerificationWrapper>
      </Fragment>
    </BasePageLayout>
  );
};

 async function _getServerSideProps(context) {
  const slug = context.query.slug as string;
  const client = initializeClient({
    context,
  });

  //fetch page data
  const queries = await Promise.allSettled([
    client.query({
      query: GetMediaPageDocument,
      variables: { slug, contentType: ContentType.LiveStream },
    }),
    client.query({
      query: GetAnnouncementDocument,
    }),
    client.query({
      query: GetNavigationDocument,
      variables: { name: 'Main' },
    }),
    client.query({
      query: GetNavigationDocument,
      variables: { name: 'Footer' },
    }),
    client.query({
      query: GetNavigationDocument,
      variables: { name: 'Profile' },
    }),
  ]);

  const pageResult = queries[0];

  if (
    pageResult.status !== 'fulfilled' ||
    !pageResult?.value?.data?.mediaBySlug
  ) {
    return {
      notFound: true,
    };
  }

  const CACHE_MAX_AGE = 60;

  context.res.setHeader(
    'Cache-Control',
    `s-maxage=${CACHE_MAX_AGE}, stale-while-revalidate=${Math.floor(
      CACHE_MAX_AGE / 2
    )}, stale-if-error=${Math.floor(CACHE_MAX_AGE * 6)}`
  );

  return addApolloState(client, { props: { slug } });
}

export default LivestreamPage;


// @ts-ignore
    export async function getServerSideProps(ctx) {
// @ts-ignore
        let res = _getServerSideProps(ctx)
// @ts-ignore
        if(typeof res.then === 'function') res = await res
// @ts-ignore
        return {
// @ts-ignore
          
// @ts-ignore
          ...res,
// @ts-ignore
          props: {
// @ts-ignore
            ...(res.props || {}),
// @ts-ignore
            ...(await __loadNamespaces({
// @ts-ignore
              ...ctx,
// @ts-ignore
              pathname: '/livestream/[slug]',
// @ts-ignore
              loaderName: 'getServerSideProps',
// @ts-ignore
              ...__i18nConfig,
// @ts-ignore
              loadLocaleFrom: (l, n) => import(`@next-translate-root/locales/${l}/${n}`).then(m => m.default),
// @ts-ignore
            }))
// @ts-ignore
          }
// @ts-ignore
        }
// @ts-ignore
    }
// @ts-ignore
  